import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Loader2 } from "lucide-react";

import { useTran } from "@/hooks";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useCompanyStore } from "../hooks";
import CompanySetupWizard from "./CompanySetupWizard";

const CompanySetupDialog = ({ open, user, onClose, onCompleted }) => {
  const { t } = useTran();
  const [loading, setLoading] = useState(false);
  const { fetchCompany, company, reset, setCompany, companyLoading } =
    useCompanyStore();

  const companyId = useMemo(
    () =>
      user?.companyId ??
      user?.company?.id ??
      user?.company?._id ??
      user?.company?.companyId ??
      user?.companyDetails?.id ??
      user?.companyDetails?._id ??
      user?.companyDetails?.companyId ??
      null,
    [user],
  );

  useEffect(() => {
    if (!open) {
      setLoading(false);
      return;
    }

    if (!companyId) {
      reset({ persist: false });
      setCompany(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    fetchCompany({ force: true, companyId })
      .catch(() => {})
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, companyId, fetchCompany, reset, setCompany]);

  const handleClose = () => {
    setLoading(false);
    onClose?.();
  };

  const handleCompleted = () => {
    onCompleted?.();
    handleClose();
  };

  const isLoading = loading || companyLoading;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent className="h-[90vh] w-[95vw] max-w-5xl overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center gap-2 p-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {t("Loading company details...")}
            </span>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-6">
            <CompanySetupWizard
              isDialog
              companyId={companyId}
              companyData={company}
              onCompleted={handleCompleted}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default observer(CompanySetupDialog);
